import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import React, { FormEventHandler, useEffect, useMemo, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { SurveyEmployeeInput } from '@/__generated__/employmentSpecialRelationshipsPastMutation.graphql';
import { employmentSpecialRelationshipsPastQuery, YesNoBlank } from '@/__generated__/employmentSpecialRelationshipsPastQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { AnswerList, CTA } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { SpecialRelationshipTitleIndicator } from '@/components/survey/employment/special-relationships/SpecialRelationshipTitleIndicator';
import { SpecialRelationshipsDescription } from '@/components/survey/employment/special-relationships/SpecialRelationshipsDescription';
import { SpecialRelationshipsSearchEmployee } from '@/components/survey/employment/special-relationships/SpecialRelationshipsSearchEmployee';
import { WhyNeedSpecialRelationship } from '@/components/survey/employment/special-relationships/WhyNeedSpecialRelationship';
import { updateSpecialRelationshipsPastQuery } from '@/graphql/mutation/survey/employment';
import { specialRelationshipsPastQuery } from '@/graphql/query/survey/employment';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertEmployment } from '@/lib/hooks/api/mutation/survey/use-upsert-employment';
import { employeesAtom, imputedYearAtom } from '@/lib/stores/survey/common';

const answers: { value: YesNoBlank; label: string }[] = [
  { value: 'no', label: '아니요, 없었어요' },
  { value: 'yes', label: '네' }
];

export const SpecialRelationshipsContainer = ({ queryRef }: { queryRef: PreloadedQuery<employmentSpecialRelationshipsPastQuery> }) => {
  const { getSurveyEmploymentAnswer } = usePreloadedQuery<employmentSpecialRelationshipsPastQuery>(specialRelationshipsPastQuery, queryRef);
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const [upsertEmploymentAnswer, isLoading] = useUpsertEmployment(updateSpecialRelationshipsPastQuery);
  const imputedYear = useAtomValue(imputedYearAtom);
  const employees = useAtomValue(employeesAtom);

  const [selectedAnswer, setSelectedAnswer] = useState<YesNoBlank>('blank');
  const [selectedEmployeeSet, setSelectedEmployeeSet] = useState<Set<string>>(new Set());

  const prevAnswer = useMemo(
    () => getSurveyEmploymentAnswer.employmentAnswer?.hasSpecialRelationshipsInThePast || 'blank',
    [getSurveyEmploymentAnswer.employmentAnswer]
  );
  const prevSpecialRelationshipsEmployeesIds = useMemo(
    () => getSurveyEmploymentAnswer.employmentAnswer?.specialRelationshipsInThePast?.map(({ indvTin }) => indvTin) || [],
    [getSurveyEmploymentAnswer.employmentAnswer]
  );
  const isAnswerYes = useMemo(() => selectedAnswer === 'yes', [selectedAnswer]);
  const imputedPeriod = getImputedPeriod(imputedYear, 6);
  const selectedEmployeesCount = selectedEmployeeSet.size;

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    sendClickEvent('survey_special-relationships_confirm', eventAttributes);

    const specialRelationshipsEmployees: SurveyEmployeeInput[] = employees
      .filter(({ id }) => selectedEmployeeSet.has(id))
      .map(({ id, birthday, name, workedYears }) => ({
        indvTin: id,
        ieNm: name,
        birthday: birthday || undefined,
        attrYears: workedYears
      }));
    upsertEmploymentAnswer({
      variables: {
        hasSpecialRelationshipsInThePast: selectedAnswer,
        specialRelationshipsInThePast: selectedAnswer === 'yes' ? specialRelationshipsEmployees : [],
        specialRelationshipsCurrently: selectedAnswer === 'no' ? [] : undefined
      },
      onCompleted: () => {
        if (selectedAnswer === 'yes') {
          router.push(SURVEY_PAGES.대표특관자_과거);
        } else {
          router.push(SURVEY_PAGES.대표특관자_현재);
        }
      }
    });
  };

  useEffect(() => {
    if (prevAnswer !== 'blank' && selectedAnswer === 'blank') {
      setSelectedAnswer(prevAnswer);
    }
    if (prevSpecialRelationshipsEmployeesIds.length > 0) {
      setSelectedEmployeeSet(new Set(prevSpecialRelationshipsEmployeesIds));
    }
  }, [prevAnswer, selectedAnswer, prevSpecialRelationshipsEmployeesIds]);

  return (
    <>
      <SurveyTopNavigation needConfirm={isAnswerYes} leftClickEvent={'survey_special-relationships_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form onSubmit={handleSubmit}>
          <article>
            <SpecialRelationshipTitleIndicator />
            <h2
              className={common.content_title}
            >{`${sliceYear(imputedPeriod[0])}년도부터 ${sliceYear(imputedPeriod[imputedPeriod.length - 1])}년도까지 직원 중 대표님의 배우자나 가족, 친인척 등 특수관계인이 있었나요?`}</h2>
            <section className={'flex flex-col gap-3'}>
              <AnswerList
                answers={answers}
                checkedValue={selectedAnswer}
                onChange={(value) => {
                  sendClickEvent(`survey_special-relationships_${value}`, eventAttributes);
                  setSelectedAnswer(value);
                }}
              />
              <SpecialRelationshipsDescription />
            </section>
            <WhyNeedSpecialRelationship />
            <SpecialRelationshipsSearchEmployee
              selectedAnswer={selectedAnswer}
              isCheckedEmployee={(id) => selectedEmployeeSet.has(id)}
              onToggleEmployee={(id) => {
                sendClickEvent('survey_special-relationships_select-employee');
                if (selectedEmployeeSet.has(id)) {
                  selectedEmployeeSet.delete(id);
                } else {
                  selectedEmployeeSet.add(id);
                }
                setSelectedEmployeeSet(new Set(selectedEmployeeSet));
              }}
            />
          </article>

          {selectedAnswer !== 'blank' && (
            <CTA type={'submit'} disabled={(isAnswerYes && selectedEmployeesCount === 0) || isLoading}>
              {(function () {
                if (isAnswerYes && selectedEmployeesCount > 0) return `${selectedEmployeesCount}명 선택 완료`;
                if (isAnswerYes) return '해당 직원을 모두 선택해주세요';
                return '다음';
              })()}
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};

const sliceYear = (fullYear?: string) => fullYear?.slice(-2) || '';
const getImputedPeriod = (imputedYear: string, few: number = 5) => {
  let year = Number(imputedYear);
  const years: number[] = [];
  for (let i = 0; i < few; i++) {
    years.push(year);
    year--;
  }
  return years.sort((a, b) => a - b).map((year) => `${year}`);
};
